/**
 * The lists of a search (or of the firm), one row per company list. Same frame
 * as People Lists' `ImportsTable` (Trello #831): the same columns in the same
 * order (Date · title · Search · count · Imported by · Actions), the same
 * sortable headers, date format, own-row accent and "Imported by" name, and a
 * row that opens the list (Enter/Space too) with the opener's URL in
 * `location.state.from` for the Back link.
 *
 * Where it differs, and why:
 * - no Type column: a company list has no lifecycle tags. How a list was born
 *   (the old "Made by" column) is on the list's own page;
 * - the one action is Download, the list's CSV export (People Lists downloads
 *   the original file). The row has nothing like Company Map or AI Review to
 *   feed into, so there are no other icons.
 */
import type { CompanyListSummary } from "@nu-assist/contracts";
import { ProgressBar } from "@nu-assist/ui";
import { ArrowDown, ArrowUp, ArrowUpDown, Download } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import type { CompanyListSortKey } from "../../clients/companyLists.client";
import { formatDate } from "../../routes/research/imports/ImportsTable";
import { ImportSearchLinks } from "../imports/selection/ImportSearchLinks";
import { companyListPath } from "./paths";
import type { ListSort } from "./queries";

interface CompanyListsTableProps {
  items: readonly CompanyListSummary[];
  /** Hide the Search column inside a search, where every row shares it. */
  showSearch: boolean;
  /**
   * A name, owner or search filter is on. An empty page then means "nothing
   * matches", not "there are no lists", and says so — the same split People
   * Lists makes (`ImportsTable`'s "No people lists match…").
   */
  filtered?: boolean;
  /** The caller's email: their own rows get the accent, as on People Lists. */
  meEmail?: string;
  sort?: ListSort;
  onSort?: (key: CompanyListSortKey) => void;
  /** The list's CSV export. Absent = no Actions column. */
  onDownload?: (row: CompanyListSummary) => void;
}

type ColDef = { label: string; sortKey?: CompanyListSortKey; numeric?: boolean };

const td = "px-3 py-2 text-body";

function isOwnRow(email: string, meEmail: string): boolean {
  return !!meEmail && email.trim().toLowerCase() === meEmail.trim().toLowerCase();
}

export function CompanyListsTable({
  items,
  showSearch,
  filtered = false,
  meEmail = "",
  sort,
  onSort,
  onDownload,
}: CompanyListsTableProps) {
  const navigate = useNavigate();
  const location = useLocation();

  if (items.length === 0 && filtered) {
    return (
      <div
        data-testid="company-lists-no-match"
        className="rounded-md border border-border bg-surface px-4 py-12 text-center text-body text-text-secondary"
      >
        No company lists match the current filter / search.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="company-lists-empty"
        className="rounded-md border border-border bg-surface px-4 py-12 text-center text-body text-text-secondary"
      >
        No company lists yet. Export a PitchBook search with Chopbook, import a CSV, or start an
        empty list.
      </div>
    );
  }

  const columns: ColDef[] = [
    { label: "Date", sortKey: "created_at" },
    { label: "Company list title", sortKey: "name" },
    ...(showSearch ? [{ label: "Search" }] : []),
    { label: "Companies", sortKey: "company_count", numeric: true },
    { label: "Imported by", sortKey: "created_by_email" },
    ...(onDownload ? [{ label: "Actions" }] : []),
  ];

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
      <table className="w-full border-collapse" data-testid="company-lists-table">
        <thead>
          <tr className="border-b border-border text-left">
            {columns.map((col) => {
              const active = !!col.sortKey && sort?.key === col.sortKey;
              const sortable = !!col.sortKey && !!onSort;
              const dir = sort?.dir ?? "asc";
              return (
                <th
                  key={col.label}
                  scope="col"
                  className="text-label font-semibold text-text-secondary"
                  aria-sort={
                    active
                      ? dir === "asc"
                        ? "ascending"
                        : "descending"
                      : sortable
                        ? "none"
                        : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={`inline-flex w-full cursor-pointer select-none items-center gap-1 px-3 py-2 hover:text-text ${
                        col.numeric ? "justify-end" : "text-left"
                      }${active ? " text-accent" : ""}`}
                      onClick={() => col.sortKey && onSort?.(col.sortKey)}
                    >
                      {col.label}
                      {active ? (
                        dir === "asc" ? (
                          <ArrowUp aria-hidden="true" className="h-3 w-3 text-accent" />
                        ) : (
                          <ArrowDown aria-hidden="true" className="h-3 w-3 text-accent" />
                        )
                      ) : (
                        <ArrowUpDown aria-hidden="true" className="h-3 w-3 text-text-muted" />
                      )}
                    </button>
                  ) : (
                    <span className={`block px-3 py-2${col.numeric ? " text-right" : ""}`}>
                      {col.label}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const own = isOwnRow(row.created_by_email, meEmail);
            const open = () =>
              navigate(companyListPath(row.list_id), {
                state: { from: `${location.pathname}${location.search}` },
              });
            const settling = row.status === "uploaded" || row.status === "parsing";
            return (
              <tr
                key={row.list_id}
                tabIndex={0}
                aria-label={`Open company list ${row.name}`}
                data-testid="company-list-row"
                data-own={own ? "true" : "false"}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                  }
                }}
                className="cursor-pointer border-b border-border last:border-b-0 hover:bg-row-hover focus:bg-row-hover focus:outline-none"
              >
                <td className={`${td} whitespace-nowrap text-text-secondary`}>
                  {formatDate(row.created_at)}
                </td>
                <td className={`${td} text-text`}>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className={own ? "font-semibold" : ""}>{row.name}</span>
                    {settling && (
                      // Fixed width: the shared bar fills its parent, and this
                      // sits inline after the list name.
                      <div className="w-10 shrink-0">
                        <ProgressBar indeterminate live label="Importing companies" />
                      </div>
                    )}
                    {row.status === "failed" && (
                      <span className="rounded-pill bg-error-muted px-2 py-0.5 text-caption text-error">
                        Import failed
                      </span>
                    )}
                  </span>
                </td>
                {showSearch && (
                  <td className={`${td} text-text-secondary`}>
                    <ImportSearchLinks
                      searchIdThrive={row.search_id_thrive ?? null}
                      searchName={row.search_name ?? null}
                      testIdPrefix={`company-list-search-${row.list_id}`}
                      newTab={false}
                      wrapSearchName
                      className="max-w-56"
                    />
                  </td>
                )}
                <td className={`${td} text-right tabular-nums text-text`}>
                  {row.company_count.toLocaleString()}
                </td>
                <td
                  data-testid="company-list-imported-by"
                  className={own ? `${td} font-semibold text-accent` : `${td} text-text-secondary`}
                >
                  {row.created_by_name ?? row.created_by_email}
                </td>
                {onDownload && (
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      aria-label={`Download ${row.name} as CSV`}
                      title="Download CSV"
                      data-testid="company-list-download"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload(row);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-center rounded-md p-1.5 text-text-secondary hover:bg-accent-muted hover:text-accent"
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
